import { CpfInput } from '@/_components/inputs/cpf-input'
import { PhoneInput } from '@/_components/inputs/phone-input'
import {
  Button,
  DialogClose,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/_components/ui'
import type { TCreateGuarantorForm } from '@/_schemas/create-guarantor'
import { CreateGuarantorFormSchema } from '@/_schemas/create-guarantor'
import { InputGroup } from '@/features/_components/input-group'
import { Text, Title } from '@/features/_components/ui/typography'
import { ApiQueryError, mutations } from '@/features/_lib/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { CreateConfirmDialog } from '../../../_components/create-confirm-dialog'

type CreateGuarantorDialogProps = {
  setIsDialogOpen: (isOpen: boolean) => void
  onChange?(): void
  onCollectData?: (data: TCreateGuarantorForm) => void
}

export const CreateGuarantorDialog = ({
  setIsDialogOpen,
  onChange,
  onCollectData,
}: CreateGuarantorDialogProps) => {
  const params = useParams({ strict: false }) as { applicationId: string }
  const queryClient = useQueryClient()
  const createGuarantor = useMutation(mutations.applications.createGuarantor(queryClient))

  const form = useForm<TCreateGuarantorForm>({
    defaultValues: {
      name: '',
      cpf: '',
      email: '',
      phone: '',
    },
    resolver: zodResolver(CreateGuarantorFormSchema),
  })

  const isPending = createGuarantor.isPending

  const onSubmit = (data: TCreateGuarantorForm) => {
    if (onCollectData) {
      onCollectData(data)
      setIsDialogOpen(false)
      return
    }

    createGuarantor.mutate(
      {
        applicationId: params.applicationId,
        data: {
          ...data,
          address: null,
          birthDate: null,
          motherName: null,
          estimatedIncome: null,
        },
      },
      {
        onSuccess: () => {
          onChange?.()
          setIsDialogOpen(false)
        },
        onError: (error) => {
          toast.error(
            error instanceof ApiQueryError
              ? (error.apiError.detail ?? error.message)
              : 'Erro ao cadastrar avalista',
          )
        },
      },
    )
  }

  return (
    <Form {...form}>
      <div className="flex flex-col items-center justify-center gap-2">
        <Title>Cadastrar Avalista</Title>
        <Text>Insira as informações abaixo</Text>
      </div>
      <form
        className="scrollbar flex max-h-[500px] flex-col gap-4 overflow-y-auto p-2"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <h2 className="font-bold">Identificação</h2>
        <InputGroup className="flex flex-row items-center">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="required-field">Nome completo</FormLabel>
                <FormControl>
                  <Input placeholder="Nome completo do avalista" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </InputGroup>
        <InputGroup>
          <FormField
            control={form.control}
            name="cpf"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="required-field">CPF</FormLabel>
                <FormControl>
                  <CpfInput
                    placeholder="CPF do avalista"
                    {...field}
                    disabled={form.formState.isSubmitting}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </InputGroup>

        <InputGroup>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel className="required-field">E-mail</FormLabel>
                <FormControl>
                  <Input
                    placeholder="E-mail do avalista"
                    {...field}
                    disabled={form.formState.isSubmitting}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex w-1/2 flex-col gap-2">
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="required-field">Telefone para contato</FormLabel>
                  <FormControl>
                    <PhoneInput
                      onChange={field.onChange}
                      value={field.value}
                      getInputRef={field.ref}
                      disabled={form.formState.isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </InputGroup>

        <div className="flex w-full justify-center gap-3">
          <DialogClose asChild>
            <Button
              variant="ghost"
              className="font-bold hover:bg-transparent hover:text-brand-grayscale-12"
              type="button"
            >
              Cancelar
            </Button>
          </DialogClose>
          <CreateConfirmDialog
            guarantorOrPatient="avalista"
            handleSubmit={form.handleSubmit(onSubmit)}
            isPending={isPending}
          />
        </div>
      </form>
    </Form>
  )
}
